import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ButtonLink } from '@/components/ui/button-link';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useNotificationSocket,
} from '@/hooks';
import { ROUTES } from '@/constants';
import {
  formatNotificationTime,
  getNotificationTypeStyles,
} from '@/constants/notifications';
import { cn } from '@/lib/utils';

function NotificationBell() {
  useNotificationSocket();

  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { data } = useNotifications({ limit: 10, sortBy: undefined });
  const markReadMutation = useMarkNotificationRead();

  const notifications = data?.notifications || [];

  const handleOpen = (notification) => {
    if (!notification.read) {
      markReadMutation.mutate(notification._id);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
          className="relative"
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          {unreadCount > 0 && (
            <span
              className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold text-white motion-reduce:animate-none"
              aria-hidden="true"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[min(100vw-2rem,22rem)] p-0">
        <DropdownMenuLabel className="flex items-center justify-between px-3 py-2">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="tabular-nums">
              {unreadCount} unread
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No notifications yet
          </div>
        ) : (
          <ul className="max-h-80 overflow-y-auto" aria-label="Recent notifications">
            {notifications.map((item) => (
              <li key={item._id}>
                <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                  <Link
                    to={item.actionUrl || ROUTES.NOTIFICATIONS}
                    onClick={() => handleOpen(item)}
                    className={cn(
                      'block w-full border-l-4 px-3 py-3 text-left transition-colors hover:bg-muted motion-reduce:transition-none',
                      getNotificationTypeStyles(item.type),
                      !item.read && 'bg-muted/40'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium leading-snug">{item.title}</p>
                      {!item.read && (
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />
                      )}
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{item.message}</p>
                    <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
                      <span>{item.category}</span>
                      <span aria-hidden="true">·</span>
                      <time dateTime={item.createdAt}>{formatNotificationTime(item.createdAt)}</time>
                    </div>
                  </Link>
                </DropdownMenuItem>
              </li>
            ))}
          </ul>
        )}

        <DropdownMenuSeparator />
        <div className="p-2">
          <ButtonLink to={ROUTES.NOTIFICATIONS} variant="secondary" size="sm" className="w-full">
            View all notifications
          </ButtonLink>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default NotificationBell;
